import type { EnhancedRendererRect } from "./wordbox";

/**
 * @file This is about joining the highlighting of words if they fit within a certain bounds
 */
 

/**
 * Interates through rects. It takes each rect one after the other and sees whether the preceeding
 * word is very close to it in the horizontal access. This is a way of trying to remove spaces by
 * "gluing words" together. There are some small caveats.
 * 
 * We are assuming that all the boxes are lined up against each other, so we only really have to compare
 * each box to its next box.
 * 
 * We are only gluing rects that are on the same "line", which means it's possible sometimes that rect
 * will have a horizontal distance less than {@link spaceThreshold} to the previous rect, even though the rect
 * is at a different vertical level and therefore should *not* be joined. Yet, at the same time, it's not reliable
 * for us to use the exact vertical position of the rects to determine whether two rects are on the same line
 * reliably because the vertical position might differ ever so slightly.
 * 
 * For this reason, we have {@link verticalLeniancy} which gives us some buffer room for vertical alignment as we
 * progress through the horizontal. As we progress, the rect that's being created as smaller rects are being joined
 * should take the bigger of the two. This means that if one rects top is higher than the previous, the new rect
 * should have the top equal to the bigger one, and same for bottom.
 * 
 * @param rects the array of rects to process
 * @param spaceThreshold the amount of pixels which, if the distance between the two rects is close enough, will collapse the rect into one
 * @param verticalLeniancy the amount of leeway we give when determining whether a rect is on the same horizontal line or not
 * @returns {RendererRect[]} glued rects
 */
export function glueRendererRects(rects : EnhancedRendererRect[], spaceThreshold : number, verticalLeniancy : number) : EnhancedRendererRect[] {
    const gluedRects: EnhancedRendererRect[] = [];
    
    for (const rect of rects) {
        if (!gluedRects.length) {
            gluedRects.push(rect)
            continue
        }

        const compare = glueTwoRects(gluedRects[gluedRects.length-1], rect, spaceThreshold, verticalLeniancy)
        if (!compare) {
            gluedRects.push(rect)
        } else {
            gluedRects[gluedRects.length-1] = compare
        }

    }
    return gluedRects;
}

/**
 * Follows from the logic of {@link glueRendererRects}
 * @param rectA 
 * @param rectB 
 * @param spaceThreshold 
 * @param verticalLeniancy 
 * @returns RendererRect of combined rect if can merge, or null if not
 */
function glueTwoRects(rectA : EnhancedRendererRect, rectB : EnhancedRendererRect, spaceThreshold : number, verticalLeniancy : number) : EnhancedRendererRect | null {
    // Check if spaceThreshold, if not return
    if ((rectB.x - rectA.right) > spaceThreshold) return null

    // Check vertical leniency - disabling bottom because of footnotes
    if (Math.abs(rectA.y - rectB.y) < verticalLeniancy /*&& Math.abs(rectA.bottom - rectB.bottom) < verticalLeniancy*/) {
        const left = rectA.x
        const right = rectB.right
        const bottom = Math.max(rectA.bottom, rectB.bottom)
        const top = Math.min(rectA.y, rectB.y)
        return {
            x: left,
            right,
            bottom,
            y: top,
            width: right-left,
            height: bottom-top,
        }
    }

    // Return null if there's no match
    return null
}